import React, { useEffect, useState } from "react";
import { Box, Typography, IconButton } from "@mui/material";
import { CheckCircle2, AlertCircle, AlertTriangle, Info, X } from "lucide-react";
import { normalizeSeverity } from "../utils/toastColors";

const TOAST_THEMES = {
  success: {
    defaultTitle: "Success",
    bg: "#EFFDF5",
    border: "#A7F3D0",
    // shadowColor: "#6EE7B7",
    iconBg: "#FFFFFF",
    iconBorder: "#A7F3D0",
    iconColor: "#059669",
    titleColor: "#064E3B",
    messageColor: "#047857",
    closeColor: "#059669",
    progressBarColor: "#334155",
    Icon: CheckCircle2,
  },
  error: {
    defaultTitle: "Error",
    bg: "#FEF2F4",
    border: "#FECDD3",
    // shadowColor: "#FDA4AF",
    iconBg: "#FFFFFF",
    iconBorder: "#FECDD3",
    iconColor: "#E11D48",
    titleColor: "#881337",
    messageColor: "#BE123C",
    closeColor: "#E11D48",
    progressBarColor: "#334155",
    Icon: AlertCircle,
  },
  warning: {
    defaultTitle: "Warning",
    bg: "#FFFBEB",
    border: "#FDE68A",
    // shadowColor: "#FCD34D",
    iconBg: "#FFFFFF",
    iconBorder: "#FDE68A",
    iconColor: "#D97706",
    titleColor: "#78350F",
    messageColor: "#B45309",
    closeColor: "#D97706",
    progressBarColor: "#334155",
    Icon: AlertTriangle,
  },
  info: {
    defaultTitle: "Info",
    bg: "#F0F9FF",
    border: "#BAE6FD",
    // shadowColor: "#7DD3FC",
    iconBg: "#FFFFFF",
    iconBorder: "#BAE6FD",
    iconColor: "#0284C7",
    titleColor: "#0C4A6E",
    messageColor: "#0369A1",
    closeColor: "#0284C7",
    progressBarColor: "#334155",
    Icon: Info,
  },
};

export const ToastCard = React.forwardRef(function ToastCard(
  {
    title,
    message,
    severity = "info",
    onClose,
    duration = 4000,
    showProgress = true,
    fileCount,
    ...otherProps
  },
  ref
) {
  const normSeverity = normalizeSeverity(severity);
  const theme = TOAST_THEMES[normSeverity] || TOAST_THEMES.info;
  const IconComponent = theme.Icon;
  const resolvedTitle = title || theme.defaultTitle;

  const [progress, setProgress] = useState(100);

  useEffect(() => {
    if (!showProgress || !duration || duration <= 0) return;

    const startTime = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, 100 - (elapsed / duration) * 100);
      setProgress(remaining);
      if (remaining <= 0) {
        clearInterval(interval);
      }
    }, 25);

    return () => clearInterval(interval);
  }, [duration, showProgress]);

  return (
    <Box
      ref={ref}
      {...otherProps}
      sx={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        minWidth: { xs: "280px", sm: "360px" },
        maxWidth: { xs: "90vw", sm: "440px" },
        backgroundColor: theme.bg,
        border: `1.5px solid ${theme.border}`,
        borderRadius: "18px",
        boxShadow: theme.shadowColor
          ? `-4px 4px 0px 0px ${theme.shadowColor}, 0 6px 20px rgba(0, 0, 0, 0.08)`
          : "0 6px 20px rgba(0, 0, 0, 0.08)",
        p: "12px 14px",
        overflow: "hidden",
        fontFamily: "'Sora', sans-serif",
        ...otherProps?.sx,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.75, flex: 1, minWidth: 0, pr: 1 }}>
        {/* White squircle icon container */}
        <Box
          sx={{
            width: 44,
            height: 44,
            minWidth: 44,
            minHeight: 44,
            borderRadius: "14px",
            backgroundColor: theme.iconBg,
            border: `1.5px solid ${theme.iconBorder}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 2px 6px rgba(0,0,0,0.03)",
          }}
        >
          <IconComponent
            size={22}
            color={theme.iconColor}
            strokeWidth={2.2}
            style={{ display: "block" }}
          />
        </Box>

        {/* Text Container */}
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "0.9375rem",
              lineHeight: 1.25,
              color: theme.titleColor,
              letterSpacing: "-0.01em",
            }}
          >
            {resolvedTitle}
          </Typography>
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: "0.84rem",
              lineHeight: 1.35,
              color: theme.messageColor,
              mt: "3px",
              wordBreak: "break-word",
            }}
          >
            {message}
            {message === "Files uploaded successfully!" && fileCount > 0 && (
              <span style={{ marginLeft: "6px", fontWeight: 700 }}>
                ({fileCount} file{fileCount > 1 ? "s" : ""})
              </span>
            )}
          </Typography>
        </Box>
      </Box>

      {/* Close Button */}
      {onClose && (
        <IconButton
          size="small"
          onClick={onClose}
          sx={{
            color: theme.closeColor,
            p: "4px",
            alignSelf: "flex-start",
            mt: "-2px",
            "&:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.05)",
            },
          }}
        >
          <X size={17} strokeWidth={2.4} />
        </IconButton>
      )}

      {/* Timer Progress Bar */}
      {showProgress && duration > 0 && (
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            height: "3.5px",
            width: `${progress}%`,
            backgroundColor: theme.progressBarColor,
            borderBottomLeftRadius: "18px",
            transition: "width 25ms linear",
          }}
        />
      )}
    </Box>
  );
});

export default ToastCard;
